/** Оформление хука в порядке макета; старые ключи card/paper/highlight/frame сохранены для проектов. */
export const HOOK_STYLES = ['card', 'paper', 'highlight', 'liquid', 'frame', 'classic', 'brackets', 'wave', 'emphasis'] as const;
export type HookDesign = (typeof HOOK_STYLES)[number];
export const HOOK_STYLE_LABELS: Record<HookDesign, string> = {
  card: 'Линия', paper: 'Лента', highlight: 'Хайлайт', liquid: 'Жидкий хайлайт', frame: 'Рамка',
  classic: 'Классика', brackets: 'Скобки', wave: 'Волна', emphasis: 'Пара',
};
/** Стили на выбор. «Лента» (paper) убрана из выбора, но остаётся в схеме и рендере для старых проектов. */
export const HOOK_STYLE_CHOICES = ['card', 'highlight', 'liquid', 'frame', 'classic', 'brackets', 'wave', 'emphasis'] as const satisfies readonly HookDesign[];
/**
 * Подложка под строками субтитров: без неё, белая (текст тёмный) или чёрная. Она же включает
 * тёмную плашку хука у Линии, Ленты и Рамки — без смены её цвета.
 */
export const SUBTITLE_BOXES = ['none', 'white', 'black'] as const;
export type SubtitleBox = (typeof SUBTITLE_BOXES)[number];
export const SUBTITLE_BOX_LABELS: Record<SubtitleBox, string> = { none: 'Нет', white: 'Белая', black: 'Черная' };

/** Размер субтитров. Множитель кегля — SUBTITLE_SIZE_FACTORS в typography. Хук не меняется. */
export const SUBTITLE_SIZES = ['small', 'medium', 'large'] as const;
export type SubtitleSize = (typeof SUBTITLE_SIZES)[number];
export const SUBTITLE_SIZE_LABELS: Record<SubtitleSize, string> = { small: 'Мелкий', medium: 'Средний', large: 'Большой' };

/** Ускорение речи и видео в готовом ролике. Монтаж и время врезок хранятся в 1x. */
export const PLAYBACK_SPEEDS = [1, 1.2, 1.5, 1.7, 2] as const;
export type PlaybackSpeed = (typeof PLAYBACK_SPEEDS)[number];

/** Акцент в хуке: без цвета, цветом подсветки или цветом и заглавными (только акцентная строка). */
export const HOOK_ACCENTS = ['none', 'normal', 'caps'] as const;
export type HookAccent = (typeof HOOK_ACCENTS)[number];
export const HOOK_ACCENT_LABELS: Record<HookAccent, string> = { none: 'Нет', normal: 'Обычный', caps: 'Капсом' };

/**
 * Константы без зависимостей. Отдельный вход `@reels/shared/constants` нужен
 * фронтенду: через общий индекс в бандл Mini App утягивался zod, а это треть
 * веса приложения, которое открывают с телефона по мобильной сети.
 */

export const MOODS = ['calm', 'upbeat', 'dramatic', 'corporate', 'lofi', 'epic'] as const;
export type Mood = (typeof MOODS)[number];

export const SUBTITLE_PRESETS = ['editorial', 'minimal', 'card', 'clean', 'karaoke', 'bold'] as const;
export type SubtitlePreset = (typeof SUBTITLE_PRESETS)[number];

/** Стили на выбор (Figma 28290:6082). «Плашка» (card) убрана из выбора, но остаётся в схеме и рендере для старых проектов. */
export const SUBTITLE_PRESET_CHOICES = ['editorial', 'minimal', 'clean', 'karaoke', 'bold'] as const satisfies readonly SubtitlePreset[];

export const SUBTITLE_LABELS: Record<SubtitlePreset | 'off', string> = {
  off: 'Без субтитров', editorial: 'Акцент', minimal: 'Минимал', card: 'Плашка',
  clean: 'Clean', karaoke: 'Karaoke', bold: 'Bold',
};

export const MIN_DURATION_S = 15;

/** Порядок стадий для расчёта прогресса на экране генерации. */
export const PROGRESS_STAGES = [
  'uploading',
  'transcribing',
  'analyzing',
  'planning',
  'rendering',
  'delivering',
  'done',
] as const;

/** Ограничения приёма файлов, проверяются и на клиенте, и на сервере. */
export const LIMITS = {
  reaction: { maxBytes: 800 * 1024 * 1024, maxDurationS: 600, minDurationS: 0.4 },
  /** Общий бюджет основных записей, включая ещё загружаемые файлы. */
  sources: { maxCount: 30, maxDurationS: 600, maxBytes: 2 * 1024 * 1024 * 1024 },
  illustrationVideo: { maxBytes: 500 * 1024 * 1024, maxDurationS: 300 },
  photo: { maxBytes: 30 * 1024 * 1024 },
  maxIllustrations: 10,
  /** Размер части multipart-загрузки. */
  uploadPartBytes: 12 * 1024 * 1024,
} as const;

/** Цвета акцента в Editorial и текущего слова в Karaoke и Bold. */
export const HIGHLIGHT_COLORS = [
  'lemon',
  'amber',
  'orange',
  'coral',
  'pink',
  'violet',
  'azure',
  'turquoise',
  'lime',
  'mint',
] as const;
export type HighlightColor = (typeof HIGHLIGHT_COLORS)[number];

export const HIGHLIGHT_COLOR_INFO: Record<HighlightColor, { label: string; hex: string }> = {
  lemon: { label: 'Лимон', hex: '#FFE500' },
  amber: { label: 'Янтарь', hex: '#FFB300' },
  orange: { label: 'Апельсин', hex: '#FF7A1A' },
  coral: { label: 'Коралл', hex: '#FF5A5F' },
  pink: { label: 'Розовый', hex: '#FF5CB8' },
  violet: { label: 'Фиолет', hex: '#B08CFF' },
  azure: { label: 'Лазурь', hex: '#3EC1FF' },
  turquoise: { label: 'Бирюза', hex: '#2EE6C5' },
  lime: { label: 'Лайм', hex: '#B8F542' },
  mint: { label: 'Мята', hex: '#6CF0A8' },
};

/**
 * Шрифты субтитров, хука и обложки. Файлы кладёт scripts/fetch-fonts.sh: статические
 * начертания с кириллицей. Превью в Mini App грузит те же локальные файлы.
 * hasBold = false: у шрифта одно начертание, и libass не должен синтезировать жирность.
 */
export const SUBTITLE_FONTS = ['douyinsans', 'montserrat', 'evolventa', 'evolventa_bold', 'oswald', 'tiktoksans', 'delagothic', 'neucha', 'daysone', 'orelegaone',
  'comfortaa', 'raleway', 'playfair', 'inter'] as const;
export type SubtitleFont = (typeof SUBTITLE_FONTS)[number];

export interface SubtitleFontInfo {
  label: string;
  /** Имя семейства, как его видят libass и браузер. */
  family: string;
  /** Файл в FONTS_DIR. */
  file: string;
  hasBold: boolean;
  /** CSS em / libass Windows em: same visual size in modern caption previews. */
  assScale: number;
  /** Настройки стиля «Акцент» из макета; без них работает общая формула по высоте прописных. */
  type?: SubtitleTypeProfile;
}

/**
 * Типографика стиля «Акцент» для шрифта (Figma 28275:5941). Кегли — в пикселях кадра
 * шириной 230, как в макете и превью; межбуквенный интервал — в долях кегля.
 * context — поясняющий текст рядом с акцентом, plain — страница без акцента.
 */
export interface SubtitleTypeProfile {
  context: number; contextTracking: number;
  plain: number; plainTracking: number;
  accent: number; accentTracking: number;
}
/** Ширина кадра, в которой заданы кегли SubtitleTypeProfile. */
export const SUBTITLE_TYPE_FRAME = 230;
const ACCENT_SIZE = 28.229;

export const SUBTITLE_FONT_INFO: Record<SubtitleFont, SubtitleFontInfo> = {
  douyinsans: { label: 'Douyin Sans', family: 'DouyinSans Bold', file: 'DouyinSansBold.ttf', assScale: 0.831947, hasBold: false },
  montserrat: { label: 'Montserrat', family: 'Montserrat Bold', file: 'Montserrat-Bold.ttf', assScale: 0.640205, hasBold: false },
  evolventa: { label: 'Evolventa', family: 'Evolventa', file: 'Evolventa-Regular.ttf', assScale: 0.751836, hasBold: false },
  evolventa_bold: { label: 'Evolventa Bold', family: 'Evolventa Bold', file: 'Evolventa-Bold.ttf', assScale: 0.751836, hasBold: false },
  oswald: { label: 'Oswald', family: 'Oswald Medium', file: 'Oswald-Medium.ttf', assScale: 0.587544, hasBold: false,
    type: { context: 12, contextTracking: 0, plain: 14, plainTracking: 0, accent: ACCENT_SIZE, accentTracking: 0 } },
  neucha: { label: 'Neucha', family: 'Neucha', file: 'Neucha.ttf', assScale: 0.8192, hasBold: false,
    type: { context: 9.688, contextTracking: 0, plain: 11, plainTracking: 0, accent: ACCENT_SIZE, accentTracking: 0 } },
  daysone: { label: 'Days One', family: 'Days One', file: 'DaysOne-Regular.ttf', assScale: 0.785577, hasBold: false },
  tiktoksans: { label: 'TikTok Sans', family: 'TikTok Sans 16pt Black', file: 'TikTokSans16pt-Black.ttf', assScale: 0.735294, hasBold: false,
    type: { context: 11, contextTracking: -0.01, plain: 11, plainTracking: -0.01, accent: ACCENT_SIZE, accentTracking: -0.06 } },
  orelegaone: { label: 'Orelega One', family: 'Orelega One', file: 'OrelegaOne-Regular.ttf', assScale: 0.956786, hasBold: false },
  delagothic: { label: 'Dela Gothic One', family: 'Dela Gothic One', file: 'DelaGothicOne-Regular.ttf', assScale: 0.690608, hasBold: false,
    type: { context: 11, contextTracking: 0, plain: 11, plainTracking: -0.01, accent: ACCENT_SIZE, accentTracking: 0 } },
  comfortaa: { label: 'Comfortaa', family: 'Comfortaa Bold', file: 'Comfortaa-Bold.ttf', assScale: 0.618429, hasBold: false,
    type: { context: 9.688, contextTracking: -0.01, plain: 11, plainTracking: -0.01, accent: ACCENT_SIZE, accentTracking: 0 } },
  raleway: { label: 'Raleway', family: 'Raleway Medium', file: 'Raleway-Medium.ttf', assScale: 0.851789, hasBold: false,
    type: { context: 10, contextTracking: -0.03, plain: 11, plainTracking: -0.03, accent: ACCENT_SIZE, accentTracking: -0.05 } },
  playfair: { label: 'Playfair Display', family: 'Playfair Display Medium', file: 'PlayfairDisplay-Medium.ttf', assScale: 0.70922, hasBold: false,
    type: { context: 12, contextTracking: 0, plain: 14, plainTracking: -0.01, accent: ACCENT_SIZE, accentTracking: 0 } },
  inter: { label: 'Inter', family: 'Inter ExtraBold', file: 'Inter-ExtraBold.ttf', assScale: 0.826473, hasBold: false,
    type: { context: 12, contextTracking: -0.03, plain: 14, plainTracking: -0.03, accent: ACCENT_SIZE, accentTracking: -0.03 } },
};

/** Шрифты, которые предлагаются на выбор, в порядке макета. Остальные — только для старых проектов. */
export const SUBTITLE_FONT_CHOICES = ['comfortaa', 'tiktoksans', 'delagothic', 'neucha', 'raleway', 'playfair', 'oswald', 'inter'] as const satisfies readonly SubtitleFont[];

/** Базовая гарнитура всех стилей субтитров. */
export const SUBTITLE_PRESET_FONT: Record<SubtitlePreset, SubtitleFont> = {
  minimal: 'tiktoksans', card: 'tiktoksans', editorial: 'tiktoksans',
  clean: 'tiktoksans', karaoke: 'tiktoksans', bold: 'tiktoksans',
};

/** Старые проекты открываются с доступной гарнитурой вместо ошибки схемы. */
export const LEGACY_SUBTITLE_FONTS: Record<string, SubtitleFont> = {
  auto: 'tiktoksans',
  ptsans: 'tiktoksans', firasans: 'tiktoksans',
  unbounded: 'daysone', russoone: 'delagothic', ubuntu: 'tiktoksans', ptserif: 'orelegaone',
  alegreya: 'neucha', ptcaption: 'tiktoksans', rubikmono: 'delagothic',
};

/** Срок хранения результата в днях. */
export const PROJECT_TTL_DAYS = 90;

/**
 * Значения настроек по умолчанию. Совпадение со схемой проверяет тест
 * settings.test.ts, поэтому литерал и схема не разъедутся.
 */
export const DEFAULT_SETTINGS = {
  editingMode: 'full',
  duration: 'auto',
  hook: true,
  hookStyle: 'card',
  hookAccent: 'normal',
  playbackSpeed: 1,
  cleanPauses: true,
  subtitles: 'editorial',
  subtitlePosition: 'bottom',
  subtitleBox: 'none',
  subtitleSize: 'medium',
  hookPosition: null,
  subtitleSound: true,
  subtitleDepth: false,
  accentZooms: false,
  highlight: 'lemon',
  font: 'tiktoksans',
  music: 'off',
  musicVolume: 'medium',
  framing: 'auto',
  captionLanguage: 'auto',
  noiseReduction: 'auto',
  transition: 'off',
  transitionDuration: 0.3,
  colorFilter: 'off',
  colorIntensity: 70,
} as const;
